import { inject, Injectable } from '@angular/core';
import { hasActionsExecuting } from '@ngxs-labs/actions-executing';
import { Store } from '@ngxs/store';
import { combineLatest, map, Observable } from 'rxjs';
import {
  LoadAvailableDashboard,
  LoadDashboardEmbedGuestToken,
  RefreshDashboardEmbedGuestToken,
  ResetState,
  SelectDashboard,
  SetResourceTimeRange
} from '../actions/dashboard.actions';
import { DashboardState } from '../states/dashboard.state';
import { NgxPermissionsService } from 'ngx-permissions';
import { DashboardService } from '~core/services/dashboard.service';
import { ConstantsService } from '~core/services/constants.service';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';

@Injectable()
export class DashboardFacade {
  protected _store = inject(Store);
  protected _constantsService = inject(ConstantsService);
  protected _permissionsService = inject(NgxPermissionsService);

  dashboardList$ = this._store.select(DashboardState.dashboardList);
  currentResourceTimeRange$ = this._store.select(
    DashboardState.currentResourceTimeRange
  );

  currentResourceTimeRangeQuery$ = this._store.select(
    DashboardState.currentResourceTimeRangeQuery
  );

  @SelectSnapshot(DashboardState.currentResourceTimeRangeQuery)
  currentResourceTimeRangeQuery!: ReturnType<
    typeof DashboardState.currentResourceTimeRangeQuery
  >;

  permittedDashboardList$ = combineLatest([
    this._permissionsService.permissions$,
    this.dashboardList$
  ]).pipe(
    map(([permissions, dashboardList]) => {
      return DashboardService.pickPermittedDashboards(
        dashboardList,
        Object.keys(permissions)
      );
    })
  );

  dashboardListErrors$ = this._store.select(DashboardState.dashboardListErrors);

  dashboardListPaginationData$ = this._store.select(
    DashboardState.dashboardListPaginationData
  );

  selectedDashboard$ = this._store.select(DashboardState.selectedDashboard);
  dashboardGuestToken$ = this._store.select(DashboardState.dashboardGuestToken);

  dashboardGuestTokenLoadingError$ = this._store.select(
    DashboardState.dashboardGuestTokenLoadingError
  );

  dashboardLatestRefreshedGuestToken$ = this._store.select(
    DashboardState.dashboardLatestRefreshedGuestToken
  );

  selectedDashboardEmbedUrl$ = combineLatest([
    this._store.select(DashboardState.dashboardGuestToken),
    this._store.select(DashboardState.selectedDashboard)
  ]).pipe(
    map(([dashboardGuestToken, selectedDashboard]) => {
      if (dashboardGuestToken && selectedDashboard) {
        // return 'https://superset.dgi.fne.dev.veone.net/superset/dashboard/p/wWkZQ33pd6G/';
        return `${this._constantsService.environment.superSetDomainBasePathUrl}/superset/dashboard/${selectedDashboard.id}?standalone=3&guest_token=${dashboardGuestToken}&hideTitle=true`;
      }
      return null;
    })
  );

  readonly isResettingState$ = this._store.select(
    hasActionsExecuting([ResetState])
  );

  readonly isLoadingAvailableDashboard$ = this._store.select(
    hasActionsExecuting([LoadAvailableDashboard])
  );

  readonly isLoadingDashboardEmbedGuestToken$ = this._store.select(
    hasActionsExecuting([LoadDashboardEmbedGuestToken])
  );

  readonly isRefreshingDashboardEmbedGuestToken$ = this._store.select(
    hasActionsExecuting([RefreshDashboardEmbedGuestToken])
  );

  // Action Dispatchers
  resetState(
    ...params: ConstructorParameters<typeof ResetState>
  ): Observable<unknown> {
    return this._store.dispatch(new ResetState(...params));
  }

  loadAvailableDashboard(
    ...params: ConstructorParameters<typeof LoadAvailableDashboard>
  ): Observable<unknown> {
    return this._store.dispatch(new LoadAvailableDashboard(...params));
  }

  selectDashboard(
    ...params: ConstructorParameters<typeof SelectDashboard>
  ): Observable<unknown> {
    return this._store.dispatch(new SelectDashboard(...params));
  }

  loadDashboardEmbedGuestToken(
    ...params: ConstructorParameters<typeof LoadDashboardEmbedGuestToken>
  ): Observable<unknown> {
    return this._store.dispatch(new LoadDashboardEmbedGuestToken(...params));
  }

  refreshDashboardEmbedGuestToken(
    ...params: ConstructorParameters<typeof RefreshDashboardEmbedGuestToken>
  ): Observable<unknown> {
    return this._store.dispatch(new RefreshDashboardEmbedGuestToken(...params));
  }

  setResourceTimeRange(
    ...params: ConstructorParameters<typeof SetResourceTimeRange>
  ): Observable<unknown> {
    return this._store.dispatch(new SetResourceTimeRange(...params));
  }
}
